/**
 * Хост адреса ноги обязан принадлежать её площадке.
 *
 * 13.08 «открывать обе» на вилке Фонбет + Леон открыло две вкладки fon.bet:
 * у ноги Леона в адресе стояло чужое событие Фонбета, и клиент его не отвёл.
 * Точный адрес из купона или адресации побеждает ленту по лестнице точности,
 * а не по принадлежности площадке — поэтому проверка живёт здесь, на входе
 * во вкладку, а не в одном из трёх источников адреса.
 *
 * Незнакомый код не режем: зеркало приносит конторы, которых нет в манифестах,
 * и выдуманный отказ отправил бы человека искать матч руками при живой ссылке.
 * Список — копия `hosts` манифестов (`backend/forq/sources/manifests`).
 */

import { FEED_KIND, PRIVATE_MIRROR } from '../lib/feedContext';

const HOSTS: Record<string, readonly string[]> = {
  bcgame: ['bc.game'],
  rollbit: ['rollbit.com'],
  betboom: ['betboom.ru'],
  betcity: ['betcity.ru', 'ad.betcity.ru'],
  blaze: ['blaze.com'],
  bovada: ['bovada.lv'],
  buff: ['buff.bet'],
  cloudbet: ['cloudbet.com'],
  '5051x': ['5051x.com'],
  csgopositive: ['csgopositive.me', 'csgopositive.xyz'],
  dafabet: ['dafabet.com', 'sports.dafabet.com', 'als.dafabet.com', 'www.dafabet.com'],
  egb: ['egb.com', 'egamingbets.com'],
  fonbet: ['fon.bet', 'fonbet.ru'],
  ggbet: ['gg.bet'],
  // BetBurger's FortuneJack clone is presented as the JetTon account in the
  // live mirror, while its native match door may still use fortunejack.com.
  jetton: ['jetton.games', 'fortunejack.com'],
  riobet: ['riobet.com'],
  vodkabet: ['vodka.bet'],
  kalshi: ['kalshi.com'],
  leon: ['leon.ru'],
  ligastavok: ['ligastavok.ru'],
  limitless: ['limitless.exchange'],
  lootbet: ['loot.bet'],
  marathon: ['marathonbet.ru', 'marathonbet.com'],
  myriad: ['myriad.markets'],
  novig: ['novig.us'],
  olimp: ['olimp.bet'],
  onexbetru: ['1xbet.com'],
  onexbet: [
    // Дверь семьи — 1xbit-8976.pro. 22bet.ng остаётся своим хостом: линия
    // с него приходит, без него нога считалась безадресной (16.09).
    '1xbit-8976.pro',
    '1xbit.com',
    '1xbit1.com',
    '1xbit2.com',
    '1xbit3.com',
    '1xbit4.com',
    '1xbit5.com',
    '1xbit6.com',
    '1xbit7.com',
    '1xbit8.com',
    '1xbit9.com',
    '1xbit10.com',
    '22bet.ng',
    '22bet.com',
    '1xbet.com',
    '1xstavka.ru',
    '1xlite.com',
    'melbet.com',
    'melbet.ru',
    'betwinner.com',
    'megapari.com',
    'linebet.com',
    'helabet.com',
    '888starz.bet',
    'paripesa.bet',
  ],
  pinnacle: ['pinnacle.com', 'pinnacle888.com'],
  polymarket: ['polymarket.com'],
  predictfun: ['predict.fun'],
  roobet: ['roobet.com'],
  rivalry: ['rivalry.com', 'www.rivalry.com'],
  smarkets: ['smarkets.com'],
  shuffle: ['shuffle.com', 'shuffle.gg'],
  stake: ['stake.com', 'stake.bet', 'stake.games'],
  sxbet: ['sx.bet'],
  thunderpick: ['thunderpick.io'],
  vbet: ['vbet.com'],
  vivarobet: ['vivarobet.am'],
  winline: ['winline.ru'],
  zenit: ['zenit.win'],
  opinion: ['opinion.trade', 'app.opinion.trade'],
  duel: ['duel.com'],
  '1win': ['1win.pro', '1win.com'],
  duelbits: ['duelbits.com', 'www.duelbits.com'],
  welstroy: ['mell2100.life', 'mell0807.live', 'mell8752.live', 'mell9899.live'],
  sportsbet: ['sportsbet.io', 'www.sportsbet.io'],
  rainbet: ['rainbet.com'],
  betinasia: ['black.betinasia.com'],
  betfair: ['orbitxch.com', 'www.orbitxch.com', 'betfair.com', 'www.betfair.com'],
  orbitex: ['orbitxch.com', 'www.orbitxch.com', 'betfair.com', 'www.betfair.com'],
  pinup: ['pin-up.bet', 'www.pin-up.bet'],
  cdna: ['crypto.com', 'og.com'],
  cbet: ['cbet.gg', 'www.cbet.gg'],
  dexsport: ['dexsport.io', 'sportsbook.dexsport.io', 'www.dexsport.io'],
  sharpxch: ['sharpxch.com', 'www.sharpxch.com'],
};

/** Те же площадки другими кодами ленты. Знак живёт в `venues.ts`, хост — здесь. */
const ALIAS: Record<string, string> = {
  mellstroy: 'welstroy',
  buffbet: 'buff',
  '1win': 'welstroy',
  onewin: 'welstroy',
  fortunejack: 'jetton',
  betfair: 'orbitex',
  betfairsb: 'orbitex',
  onexstavka: 'onexbet',
  '1xstavka': 'onexbet',
  '1xbet': 'onexbet',
  '1xbit': 'onexbet',
  '22bet': 'onexbet',
  marathonbet: 'marathon',
  bingoboom: 'betboom',
  gg: 'ggbet',
  lut: 'lootbet',
  dafa: 'dafabet',
  sportsbetio: 'sportsbet',
  pinupbet: 'pinup',
};

function bookAlias(source: string): string {
  if ((FEED_KIND === 'main' || FEED_KIND === 'tests' || FEED_KIND === 'ru' || FEED_KIND === 'feed6' || FEED_KIND === 'feed7') && (source === '1win' || source === 'onewin')) return '1win';
  return ALIAS[source] ?? source;
}

const bare = (host: string): string => host.toLowerCase().replace(/^www\./, '');

const ONEX_DOOR_HOST = '1xbit-8976.pro';
const ONEX_DOOR_LANG = 'ru';
const PINNACLE_DOOR_HOST = 'www.pinnacle888.com';
/** Публичный ключ гостевого CDN — тот же, что `forq.sources.pinnacle`. */
const PINNACLE_GUEST_KEY = 'CmX2KcMrXuFmNg6YFbmTxE0y9CIrOi0R';
const PINNACLE_MATCHUP_API = 'https://guest.api.arcadia.pinnacle.com/0.1/matchups';
const PINNACLE_DOOR_CACHE = new Map<string, string>();
const PINNACLE_DOOR_WAIT = new Map<string, Promise<string | null>>();
const ONEX_BIT_HOSTS = new Set([
  '1xbit-8976.pro',
  '1xbit.com',
  '1xbit1.com',
  '1xbit2.com',
  '1xbit3.com',
  '1xbit4.com',
  '1xbit5.com',
  '1xbit6.com',
  '1xbit7.com',
  '1xbit8.com',
  '1xbit9.com',
  '1xbit10.com',
]);

// 27.09: `/ru/live/counter-strike/{champ}/{game}` redirects to an empty
// `/ru/esports/real/cs-2/…`; `/ru/live/esports/{champ}/{game}` renders the match.
const ONEX_ESPORT_SLUGS = new Set(['counter-strike', 'cs-2', 'cs2', 'dota-2', 'dota2', 'valorant',
  'league-of-legends', 'lol', 'rainbow-six', 'starcraft-2', 'mobile-legends', 'call-of-duty']);

/** Номер матча в хвосте `/live/.../{id}-команды` или коротком `/live/{id}`. */
function onexEventPath(pathname: string): string | null {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== undefined && /^(en|ru)$/i.test(parts[0])) parts.shift();
  if (parts[0] !== 'live' && parts[0] !== 'line') return null;
  const id = (parts[parts.length - 1] ?? '').split('-', 1)[0] ?? '';
  if (!/^\d+$/.test(id)) return null;
  if (parts.length === 4 && ONEX_ESPORT_SLUGS.has((parts[1] ?? '').toLowerCase())) parts[1] = 'esports';
  return `/${ONEX_DOOR_LANG}/${parts.join('/')}`;
}

/**
 * Pinnacle → витрина pinnacle888.
 *
 * 888 — белая этикетка SIGUNA. Официальный путь
 * `/{язык}/{спорт}/{лига}/{пара}/{id}` даёт пустую страницу.
 * Касса ждёт `/{язык}/sports/{спорт}/matchup/{лига}/{событие}/{лигаId}/{id}`.
 * `leagueId=0` сажает на список спорта (E9S4). Язык — из ссылки или двери,
 * не всегда `ru`.
 */
export function pinnacleDoorUrl(url: string): string {
  const id = pinnacleEventId(url);
  const cached = id === null ? undefined : PINNACLE_DOOR_CACHE.get(id);
  if (cached !== undefined) return cached;
  return pinnacleDoorShape(url);
}

export function resetPinnacleDoorCache(): void {
  PINNACLE_DOOR_CACHE.clear();
  PINNACLE_DOOR_WAIT.clear();
}

export function isPinnacleStorefront(url: string): boolean {
  try {
    const host = bare(new URL(url).hostname);
    return host === 'pinnacle.com' || host === 'pinnacle888.com';
  } catch {
    return false;
  }
}

export function pinnacleEventId(url: string): string | null {
  try {
    const parts = new URL(url).pathname.split('/').filter(Boolean);
    const tail = parts[parts.length - 1] ?? '';
    return /^\d+$/.test(tail) ? tail : null;
  } catch {
    return null;
  }
}

function takeLocale(parts: string[]): 'en' | 'ru' {
  let lang: 'en' | 'ru' = 'ru';
  let seen = false;
  while (parts[0] === 'en' || parts[0] === 'ru') {
    if (!seen) lang = parts[0];
    seen = true;
    parts.shift();
  }
  return lang;
}

function pinnacleDoorShape(url: string, patch?: { leagueId?: string; eventId?: string }): string {
  try {
    const next = new URL(url);
    const host = bare(next.hostname);
    if (host !== 'pinnacle.com' && host !== 'pinnacle888.com') return url;
    const parts = next.pathname.split('/').filter(Boolean);
    const lang = takeLocale(parts);
    next.protocol = 'https:';
    next.hostname = PINNACLE_DOOR_HOST;
    next.port = '';
    next.search = '';
    next.hash = '';
    const compact = parts[0] === 'compact' && parts[1] === 'sports' && parts[3] === 'matchup' && parts.length >= 8;
    const sports = parts[0] === 'sports' && parts[2] === 'matchup' && parts.length >= 7;
    if (compact || sports) {
      const leagueAt = parts.length - 2;
      const idAt = parts.length - 1;
      const current = parts[leagueAt] ?? '0';
      const keep = /^\d+$/.test(current) && current !== '0' && patch === undefined;
      if (!keep) {
        if (patch?.leagueId !== undefined) parts[leagueAt] = patch.leagueId;
        if (patch?.eventId !== undefined) parts[idAt] = patch.eventId;
      }
      next.pathname = `/${lang}/${parts.join('/')}`;
      return next.toString();
    }
    const id = patch?.eventId ?? parts[parts.length - 1] ?? '';
    if (parts.length >= 4 && /^\d+$/.test(id)) {
      const sport = parts[0]!;
      const league = parts[1]!;
      const event = parts.slice(2, -1).join('-');
      const openLeague = patch?.leagueId ?? '0';
      next.pathname = `/${lang}/sports/${sport}/matchup/${league}/${event}/${openLeague}/${id}`;
      return next.toString();
    }
    next.pathname = parts.length === 0 ? `/${lang}` : `/${lang}/${parts.join('/')}`;
    return next.toString();
  } catch {
    return `https://${PINNACLE_DOOR_HOST}/ru`;
  }
}

type PinnacleGuest = {
  id?: number;
  parentId?: number | null;
  league?: { id?: number };
};

async function fetchPinnacleMatchup(
  id: string,
  load: typeof fetch = fetch,
): Promise<PinnacleGuest | null> {
  try {
    const res = await load(`${PINNACLE_MATCHUP_API}/${id}`, {
      headers: { Accept: 'application/json', 'X-API-Key': PINNACLE_GUEST_KEY },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as PinnacleGuest;
    return body;
  } catch {
    return null;
  }
}

function rememberDoor(id: string, url: string): void {
  PINNACLE_DOOR_CACHE.set(id, url);
}

/** Лига и родитель из гостевого API. Без ответа — грамматический каркас с `0`. */
export async function resolvePinnacleDoorUrl(
  url: string,
  load: typeof fetch = fetch,
): Promise<string> {
  const id = pinnacleEventId(url);
  if (id === null) return pinnacleDoorUrl(url);
  const hit = PINNACLE_DOOR_CACHE.get(id);
  if (hit !== undefined) return hit;
  let pending = PINNACLE_DOOR_WAIT.get(id);
  if (pending === undefined) {
    pending = fetchPinnacleMatchup(id, load).then((meta) => {
      PINNACLE_DOOR_WAIT.delete(id);
      if (meta === null) return null;
      const leagueId = meta.league?.id;
      const openId = meta.parentId ?? meta.id ?? Number(id);
      if (leagueId === undefined || !Number.isFinite(leagueId) || !Number.isFinite(openId)) return null;
      const opened = pinnacleDoorShape(url, { leagueId: String(leagueId), eventId: String(openId) });
      rememberDoor(id, opened);
      rememberDoor(String(openId), opened);
      return opened;
    });
    PINNACLE_DOOR_WAIT.set(id, pending);
  }
  const opened = await pending;
  return opened ?? pinnacleDoorUrl(url);
}

export function warmPinnacleDoors(urls: readonly string[]): void {
  if (import.meta.env.MODE === 'test') return;
  if (typeof fetch !== 'function') return;
  const seen = new Set<string>();
  for (const url of urls) {
    if (!isPinnacleStorefront(url)) continue;
    const id = pinnacleEventId(url);
    if (id === null || seen.has(id) || PINNACLE_DOOR_CACHE.has(id)) continue;
    seen.add(id);
    void resolvePinnacleDoorUrl(url);
  }
}

/** Семья 1x: тот же id матча, витрина 1xBit. */
export function onexDoorUrl(url: string): string {
  // CUPIS owns a 1xBet account; preserve its verified native match address.
  if (PRIVATE_MIRROR || FEED_KIND === 'feed7') return url;
  try {
    const next = new URL(url);
    const path = onexEventPath(next.pathname);
    next.protocol = 'https:';
    next.hostname = ONEX_DOOR_HOST;
    next.port = '';
    next.pathname = path ?? `/${ONEX_DOOR_LANG}`;
    return next.toString();
  } catch {
    return `https://${ONEX_DOOR_HOST}/${ONEX_DOOR_LANG}`;
  }
}

/** Mirror redirects preserve the account. Do not generalize to every HOSTS
 * group: onexbet and vbet contain distinct operators/licensed storefronts. */
export function sameAccountMirror(a: string, b: string): boolean {
  const left = bare(a);
  const right = bare(b);
  const welstroy = (host: string) => HOSTS.welstroy!.some(
    (known) => host === known || host.endsWith(`.${known}`),
  );
  if (welstroy(left) && welstroy(right)) return true;
  return ONEX_BIT_HOSTS.has(left) && ONEX_BIT_HOSTS.has(right);
}

/**
 * Куда бросает гео-редирект той же витрины. Полоса здесь нужна: человек уже
 * на странице. Открывать как нашу контору — нет: `vbet.fr` и `vbet.nl` —
 * другие партнёры с другой ценой (`docs/kb/05-PLATFORMS-RECON/vbet-protocol.md`).
 */
const BAR_ONLY: readonly string[] = ['vbet.fr', 'vbet.nl', 'vbet.am', 'vbet.ru'];

/**
 * Корни площадок, на которых полоса расширения вшита заранее.
 *
 * `www` снимаем: и `rivalry.com`, и `www.rivalry.com` — одна контора.
 * Поддомен, явно записанный в HOSTS, остаётся отдельным корнем — лишняя
 * строка в манифесте дешевле пропущенного зеркала.
 */
export function bookApex(): string[] {
  const names = new Set<string>();
  for (const hosts of Object.values(HOSTS)) {
    for (const host of hosts) names.add(bare(host));
  }
  for (const host of BAR_ONLY) names.add(bare(host));
  return [...names].sort();
}

/** Хост — известная контора или биржа, а не случайный сайт. */
export function isBookHost(host: string): boolean {
  const name = bare(host);
  return bookApex().some((one) => name === one || name.endsWith(`.${one}`));
}

/**
 * Куда браузер вставляет полосу. Корень и `*.корень`: витрина часто живёт
 * на поддомене, а в манифесте записан только сайт.
 */
export function bookMatches(): string[] {
  return bookApex().flatMap((host) => [`https://${host}/*`, `https://*.${host}/*`]);
}

function hostOf(url: string): string | null {
  try {
    return bare(new URL(url).hostname);
  } catch {
    return null;
  }
}

function owns(source: string, host: string): boolean {
  const code = bookAlias(source);
  if (FEED_KIND === 'feed7' && code === 'leon' && (host === 'leonbets.net' || host.endsWith('.leonbets.net'))) return true;
  const allowed = HOSTS[code];
  if (allowed === undefined) return true;
  // Фид Duel живёт на поддомене витрины. Суффикс `*.duel.com` иначе
  // пустил бы sports-proxy как дверь купона.
  if (host === 'sports-proxy.duel.com') return false;
  return allowed.some((one) => {
    const domain = bare(one);
    return host === domain || host.endsWith(`.${domain}`);
  });
}

/**
 * Адрес, который можно открыть как эту площадку. `null` — входа нет: пусто,
 * битая строка или хост чужой конторы.
 */
const BCGAME_SPORTS = 'https://bc.game/sports';

/** Хаб BC.Game, не казино и не чужой бренд. Страницу матча не переписываем. */
export function bcgameDoor(url: string | null | undefined): string | null {
  if (url == null || url === '') return BCGAME_SPORTS;
  try {
    const parsed = new URL(url);
    if (bare(parsed.hostname) !== 'bc.game') return url;
    const path = parsed.pathname.replace(/\/+$/, '') || '/';
    if (path === '/' || path === '/sports' || path === '/casino' || path === '/en' || path === '/en/sports') {
      return BCGAME_SPORTS;
    }
    return url;
  } catch {
    return url;
  }
}

const ORBITEX_HOST = 'www.orbitxch.com';
const ORBITEX_EVENT = /^\/customer\/sport\/\d+\/event\/\d+$/;
const BF_SPORT: Record<string, string> = {
  soccer: '1',
  football: '1',
  tennis: '2',
  golf: '3',
  cricket: '4',
  'rugby-union': '5',
  boxing: '6',
  'horse-racing': '7',
  'motor-sport': '8',
  special: '10',
  'special-bets': '10',
  cycling: '11',
  'rugby-league': '1477',
  darts: '3503',
  athletics: '3988',
  'greyhound-racing': '4339',
  handball: '468328',
  snooker: '6422',
  'american-football': '6423',
  baseball: '7511',
  basketball: '7522',
  hockey: '7524',
  'ice-hockey': '7524',
  'australian-rules': '61420',
  'gaelic-football': '2152880',
  'gaelic-games': '2152880',
  hurling: '2152880',
  volleyball: '998917',
  bowls: '998918',
  politics: '2378961',
  'mixed-martial-arts': '26420387',
  esports: '27454571',
};

/** Orbitex матч — /customer/sport/{тип}/event/{id}. Рынок 1.id сам по себе — 404. */
export function orbitexDoor(url: string): string | null {
  try {
    const next = new URL(url);
    const host = bare(next.hostname);
    if (host !== 'betfair.com' && host !== 'orbitxch.com') return null;
    const path = next.pathname.replace(/\/+$/, '') || '/';
    if (ORBITEX_EVENT.test(path)) {
      return `https://${ORBITEX_HOST}${path}`;
    }
    const parts = path.split('/').filter(Boolean);
    const eventAt = parts.indexOf('event');
    if (eventAt >= 0 && /^\d+$/.test(parts[eventAt + 1] ?? '')) {
      const slug = parts.find((part) => BF_SPORT[part.toLowerCase()] !== undefined);
      const sport = slug === undefined ? undefined : BF_SPORT[slug.toLowerCase()];
      if (sport !== undefined) {
        return `https://${ORBITEX_HOST}/customer/sport/${sport}/event/${parts[eventAt + 1]}`;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/** Кибер CloudBet — `/en/esports/…`, не `/en/sports/…`. */
const CLOUDBET_ESPORT = new Set([
  'counter-strike',
  'dota-2',
  'league-of-legends',
  'valorant',
  'rainbow-six',
  'starcraft',
  'mobile-legends',
]);

/** Матч CloudBet — последний сегмент числовой id, не slug `event.key` (404 с 17.09). */
export function cloudbetEventUrl(url: string): boolean {
  try {
    const parts = new URL(url).pathname.split('/').filter(Boolean);
    return (
      parts.length === 5 &&
      parts[0] === 'en' &&
      (parts[1] === 'sports' || parts[1] === 'esports') &&
      /^\d+$/.test(parts[4] ?? '')
    );
  } catch {
    return false;
  }
}

/**
 * Slug `event.key` на витрине даёт 404. Живой путь — числовой id, лига без
 * префикса спорта. Без id оставляем как пришло: выдумать номер нельзя.
 */
export function cloudbetDoor(url: string | null | undefined, eid?: string | null): string | null {
  if (url == null || url === '') return url ?? null;
  try {
    const next = new URL(url);
    if (bare(next.hostname) !== 'cloudbet.com') return url;
    const parts = next.pathname.split('/').filter(Boolean);
    if (parts.length !== 5 || parts[0] !== 'en') return url;
    if (parts[1] !== 'sports' && parts[1] !== 'esports') return url;
    const sport = parts[2] ?? '';
    const competition = parts[3] ?? '';
    const last = parts[4] ?? '';
    const id = eid && /^\d+$/.test(eid) ? eid : /^\d+$/.test(last) ? last : '';
    if (!id || !sport || !competition) return url;
    const section = CLOUDBET_ESPORT.has(sport) ? 'esports' : 'sports';
    const prefix = `${sport}-`;
    const league = competition.startsWith(prefix) ? competition.slice(prefix.length) : competition;
    if (!league) return url;
    next.protocol = 'https:';
    next.hostname = 'www.cloudbet.com';
    next.port = '';
    next.pathname = `/en/${section}/${sport}/${league}/${id}`;
    next.search = '';
    next.hash = '';
    return next.toString();
  } catch {
    return url;
  }
}

/** Купон Thunderpick: `/esports/{disc}/{slug|x}/{compId}/{pair|x}/{eventId}`. */
export function thunderpickEventUrl(url: string): boolean {
  try {
    const parts = new URL(url).pathname.split('/').filter(Boolean);
    return parts.length === 6 && parts[0] === 'esports' && /^\d+$/.test(parts[3] ?? '') && /^\d+$/.test(parts[5] ?? '');
  } catch {
    return false;
  }
}

/**
 * Раздел дисциплины (`/esports/dota-2`) — не матч. Купон собираем по числам
 * с заглушкой `x` (14.09: `/x/{compId}/x/{eventId}` открыл тот же матч).
 * Без id турнира путь не выдумываем.
 */
export function thunderpickDoor(url: string | null | undefined, eid?: string | null): string | null {
  if (url == null || url === '') return url ?? null;
  try {
    const next = new URL(url);
    if (bare(next.hostname) !== 'thunderpick.io') return url;
    const parts = next.pathname.split('/').filter(Boolean);
    if (parts[0] !== 'esports' || !parts[1]) return url;
    if (thunderpickEventUrl(url)) return url;
    const eventId = eid && /^\d+$/.test(eid) ? eid : /^\d+$/.test(parts[5] ?? '') ? parts[5]! : '';
    const compId = /^\d+$/.test(parts[3] ?? '') ? parts[3]! : '';
    if (!eventId || !compId) return url;
    const compSlug = parts[2] && !parts[2].includes(' ') ? parts[2] : 'x';
    const pair = parts[4] && !parts[4].includes(' ') ? parts[4] : 'x';
    next.protocol = 'https:';
    next.hostname = 'thunderpick.io';
    next.port = '';
    next.pathname = `/esports/${parts[1]}/${compSlug}/${compId}/${pair}/${eventId}`;
    next.search = '';
    next.hash = '';
    return next.toString();
  } catch {
    return url;
  }
}

/** Дверь матча, если лента принесла раздел или slug. Остальные площадки не трогаем. */
export function eventDoor(
  source: string,
  url: string | null | undefined,
  eid?: string | null,
): string | null {
  const code = bookAlias(source);
  if (code === 'cloudbet') return cloudbetDoor(url, eid);
  if (code === 'thunderpick') return thunderpickDoor(url, eid);
  return url ?? null;
}

export function urlFor(source: string, url: string | null | undefined): string | null {
  if (typeof url !== 'string' || url === '') return null;
  const host = hostOf(url);
  if (host === null) return null;
  if (!owns(source, host)) return null;
  const code = bookAlias(source);
  // Зеркало реестра и .com гостю не дверь: .xyz отвечает 429 (DDoS-Guard),
  // .com — челлендж Cloudflare. Живой сайт — .me. Страницы матча нет.
  if (code === 'csgopositive' && host !== 'csgopositive.me') {
    return 'https://csgopositive.me/';
  }
  if (code === 'onexbet') return onexDoorUrl(url);
  if (code === 'pinnacle') return pinnacleDoorUrl(url);
  if (code === '5051x') return x5051Door(url);
  if (code === 'orbitex' || code === 'betfair') return orbitexDoor(url);
  return url;
}

// Телефон рядом, не вместо. urlFor не менять.
export { urlForPhone } from './phone/urlForPhone';

/** Короткий `/live/{sport}/{id}` у 5051x — 404; матч живёт под `/bookmaker/match/`. */
function x5051Door(url: string): string {
  try {
    const next = new URL(url);
    const hit = /^\/(live|prematch)\/([a-z0-9_]+)\/(\d+)\/?$/i.exec(next.pathname);
    if (hit === null) return url;
    next.pathname = `/bookmaker/match/${hit[1]!.toLowerCase()}/${hit[2]!.toLowerCase()}/${hit[3]}`;
    return next.toString();
  } catch {
    return url;
  }
}

/**
 * Корень площадки, когда события нет. Не выдумываем адрес незнакомой конторе
 * и не ведём на API: человек жмёт имя в строке, а не служебный хост.
 */
function siteHost(host: string): boolean {
  const name = bare(host);
  if (name.includes('sptpub.') || name.includes('swarm')) return false;
  const head = name.split('.')[0] ?? '';
  return !/api|wss|^ws$|graphql|gql|guest|realtime|clob|gamma|openapi|external/i.test(head);
}

export const MELLSTROY_SECTION = 'https://mell8752.live/betting/esports';
/** @deprecated kept for callers compiled against the old mirror name. */
export const ONEWIN_SECTION = MELLSTROY_SECTION;

export function homeOf(source: string): string | null {
  const raw = source.trim();
  if (raw === '' || raw === 'hidden') return null;
  const code = bookAlias(raw);
  // Одна дверь семьи: матч открывается на 1xbit-8976.pro, не на 22bet.
  if (code === 'onexbet') return `https://${ONEX_DOOR_HOST}/${ONEX_DOOR_LANG}`;
  if (code === 'pinnacle') return `https://${PINNACLE_DOOR_HOST}/ru`;
  if (code === 'orbitex' || code === 'betfair') return `https://${ORBITEX_HOST}/customer/`;
  // Dafa Sports and OW Sports are distinct products. This fallback is only
  // used without a supplied event URL; never rewrite an explicit OW/ALS link.
  if (code === 'dafabet') return 'https://sports.dafabet.com/en';
  // Казино-корень без /sports — пустой виджет: поиск и купон живут в разделе.
  if (code === 'bcgame') return 'https://bc.game/sports';
  if (code === 'duel') return 'https://duel.com/sports';
  if (code === 'jetton') return 'https://jetton.games/sportsbook';
  // У 1win и Mellstroy разные счета и страницы событий.
  if (code === 'welstroy') return MELLSTROY_SECTION;
  if (code === '1win') return 'https://1win.pro/betting/esports';
  // EGB: купон живёт в каталоге /play/bets, корень — лендинг без линий.
  if (code === 'egb') return 'https://egb.com/play/bets';
  const allowed =
    HOSTS[code] ??
    Object.entries(HOSTS).find(([key]) => {
      const a = key.replace(/[^a-z0-9]/g, '');
      const b = code.replace(/[^a-z0-9]/g, '');
      return b.length >= 4 && a.length >= 4 && (a === b || b.startsWith(a) || a.startsWith(b));
    })?.[1];
  if (allowed === undefined) return null;
  const needle = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
  const branded = allowed.find((one) => {
    const name = bare(one).replace(/[^a-z0-9]/g, '');
    return needle.length >= 4 && name.includes(needle);
  });
  const host = branded ?? allowed.find(siteHost) ?? allowed[0];
  if (host === undefined) return null;
  return `https://${bare(host)}/`;
}

/**
 * Кассы той же линии. Цена считается по `source`, человек открывает свой бренд.
 * Rainbet сюда не входит: третий кран без отдельного замера.
 */
export const FAMILY_CASHIERS: Record<string, readonly { code: string; host: string }[]> = {
  bcgame: [{ code: 'rollbit', host: 'rollbit.com' }],
};

/** Наши имена: дверь человека не может указывать на сканер. */
const OURS = new Set(['forq.ru']);

export interface Door {
  /** `https://csgopositive.xyz` — без пути и слэша. */
  origin: string;
  host: string;
  /** `/en` или пусто. Без завершающего слэша. */
  prefix: string;
}

function looksHost(host: string): boolean {
  if (!host.includes('.') || host.startsWith('.') || host.endsWith('.')) return false;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return false;
  return /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/.test(host);
}

/**
 * Разобрать то, что человек вписал в карандаш. Пусто — двери нет. Мусор —
 * `null`: в стор не пишем, клик остаётся каноном.
 */
export function parseDoor(raw: string | null | undefined): Door | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (/^(javascript|data|vbscript|file):/i.test(trimmed)) return null;
  let url: URL;
  try {
    url = /^https?:\/\//i.test(trimmed) ? new URL(trimmed) : new URL(`https://${trimmed}`);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:') return null;
  if (url.username !== '' || url.password !== '') return null;
  const host = url.hostname.toLowerCase();
  if (!looksHost(host)) return null;
  const apex = bare(host);
  if (OURS.has(apex) || [...OURS].some((one) => apex.endsWith(`.${one}`))) return null;
  const prefix = url.pathname.replace(/\/+$/, '');
  return { origin: `https://${host}`, host, prefix: prefix === '/' ? '' : prefix };
}

/** Как хранить и как сравнивать: origin или origin+префикс. */
export function formatDoor(door: Door): string {
  return door.prefix === '' ? door.origin : `${door.origin}${door.prefix}`;
}

/** В поле карандаша — без схемы: так же, как плейсхолдер. */
export function doorInputOf(raw: string): string {
  const door = parseDoor(raw);
  if (door === null) return raw;
  return door.prefix === '' ? door.host : `${door.host}${door.prefix}`;
}

/**
 * Подмена хоста. Путь, query и hash события живут. Свой хост через `owns`
 * не гоняем: его почти никогда нет в каталоге, и `null` хуже кривого канона.
 */
export function applyDoor(
  url: string | null | undefined,
  door: string | Door | null | undefined,
): string | null {
  if (url == null || url === '') return url ?? null;
  const parsed = typeof door === 'object' && door !== null ? door : parseDoor(door);
  if (parsed === null) return url;
  try {
    const next = new URL(url);
    next.protocol = 'https:';
    next.hostname = parsed.host;
    next.port = '';
    if (parsed.prefix !== '') {
      const path = next.pathname || '/';
      const pref = parsed.prefix;
      if (path !== pref && !path.startsWith(`${pref}/`)) {
        next.pathname = path === '/' ? `${pref}/` : `${pref}${path}`;
      }
    }
    const opened = next.toString();
    return bare(next.hostname) === 'pinnacle888.com' ? pinnacleDoorUrl(opened) : opened;
  } catch {
    return url;
  }
}

/** Канонический код площадки: алиас ленты → ключ HOSTS. */
export function canonBook(source: string): string {
  return bookAlias(source);
}

/** Все хосты каталога: PAC знает контору, даже если её нет в кадре. */
export function allBookHosts(): { source: string; host: string }[] {
  const out: { source: string; host: string }[] = [];
  for (const [source, hosts] of Object.entries(HOSTS)) {
    for (const host of hosts) out.push({ source, host: bare(host) });
  }
  return out;
}

/** Код кассы по хосту вкладки. `www` снимаем, поддомен той же витрины — та же. */
export function bookOfHost(host: string): string | null {
  const name = bare(host);
  for (const row of allBookHosts()) {
    if (name === row.host || name.endsWith(`.${row.host}`)) return row.source;
  }
  return null;
}

/** Известные имена площадки — подсказка в карандаше, не цензура. */
export function knownDoors(source: string): readonly string[] {
  const code = bookAlias(source);
  const allowed = HOSTS[code];
  if (allowed === undefined) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const host of allowed) {
    const name = bare(host);
    if (seen.has(name)) continue;
    seen.add(name);
    out.push(name);
  }
  return out;
}

/** Хост — дверь, которую человек сам вписал. */
export function isDoorHost(host: string, doors: Iterable<string>): boolean {
  const name = bare(host);
  for (const raw of doors) {
    const door = parseDoor(raw);
    if (door === null) continue;
    const one = bare(door.host);
    if (name === one || name.endsWith(`.${one}`)) return true;
  }
  return false;
}

/** Полоса и стол: каталог или дверь человека. */
export function knowsBook(host: string, doors: Iterable<string> = []): boolean {
  return isBookHost(host) || isDoorHost(host, doors);
}

/** Корень, который открывает подпись конторы, если события нет. */
export function doorHome(source: string, door?: string | null): string | null {
  const parsed = parseDoor(door ?? '');
  const base = homeOf(source);
  if (parsed === null) return base;
  if (base !== null) return applyDoor(base, parsed);
  return `${parsed.origin}${parsed.prefix}/`;
}

/**
 * URL кассы по счёту человека. Нет счёта или он закрыт — `null`, остаётся source.
 */
export function overlayUrl(
  source: string,
  url: string | null,
  accounts: Record<string, { standing: string }>,
): string | null {
  if (url == null) return null;
  const cashiers = FAMILY_CASHIERS[source];
  if (cashiers === undefined) return null;
  for (const one of cashiers) {
    const standing = accounts[one.code]?.standing;
    if (standing === undefined || standing === 'closed') continue;
    try {
      const next = new URL(url);
      next.host = one.host;
      return urlFor(one.code, next.toString());
    } catch {
      return null;
    }
  }
  return null;
}
